import { Box, Container, Grid, Typography, Divider } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';

const Footer = () => {
  const { t } = useTranslation();
  const { mode } = useTheme();

  return (
    <Box
      component="footer"
      sx={{
        background: mode === 'dark' 
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        color: 'white',
        py: { xs: 4, md: 6 },
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                color: '#f97316',
              }}
            >
              {t('footer.companyName') || 'Demis & Smart Decision Arya'}
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.6,
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: { xs: '0.875rem', md: '1rem' },
              }}
            >
              {t('footer.description') || 'Leading providers of AI-powered solutions for organizational management and smart decision making.'}
            </Typography>

            {/* Address */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: 'rgba(255, 255, 255, 0.9)',
                }}
              >
                {t('footer.address') || 'Address:'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  lineHeight: 1.5,
                }}
              >
                {t('footer.addressText') || 'No. 125, next to Karafarin Bank, after Hasht Behesht Street, Fatemi Street, Tehran'}
              </Typography>
            </Box>

            {/* Email */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: 'rgba(255, 255, 255, 0.9)',
                }}
              >
                {t('footer.email') || 'Email:'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  lineHeight: 1.5,
                }}
              >
                <Box
                  component="a"
                  href="mailto:info@demisco.com"
                  sx={{
                    color: '#3b82f6',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {t('footer.emailAddress') || 'info@demisco.com'}
                </Box>
              </Typography>
            </Box>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                color: '#f97316',
              }}
            >
              {t('footer.contact') || 'Contact Information'}
            </Typography>

            {/* Phone Numbers */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: 'rgba(255, 255, 255, 0.9)',
                }}
              >
                {t('footer.phone') || 'Phone Numbers:'}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#f97316',
                      flexShrink: 0,
                    }}
                  />
                  {t('footer.phoneNumbers.demis') || 'Demis: 031-31319280 ~ 85'}
                </Typography>
                
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#f97316',
                      flexShrink: 0,
                    }}
                  />
                  {t('footer.phoneNumbers.smartDecision') || 'Smart Decision Arya: 031-31319286 ~ 90'}
                </Typography>
                
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#f97316',
                      flexShrink: 0,
                    }}
                  />
                  {t('footer.phoneNumbers.tehran') || 'Tehran: 021-89772022'}
                </Typography>
              </Box>
            </Box>

            {/* Postal Codes */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: 'rgba(255, 255, 255, 0.9)',
                }}
              >
                {t('footer.postalCodes') || 'Postal Codes:'}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#f97316',
                      flexShrink: 0,
                    }}
                  />
                  {t('footer.postalCodeTehran') || 'Tehran: 1414713463'}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider 
          sx={{ 
            my: 3, 
            borderColor: 'rgba(255, 255, 255, 0.1)' 
          }} 
        />

        {/* Copyright */}
        <Box
          sx={{
            textAlign: 'center',
            pt: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: { xs: '0.75rem', md: '0.875rem' },
            }}
          >
            {t('footer.copyright', { year: new Date().getFullYear() }) || `© ${new Date().getFullYear()} Demis & Smart Decision Arya. All rights reserved.`}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
